import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { addressService } from '../services/addressService';
import { authService } from '../services/authService';
import { localeService } from '../services/localeService';
import {
  Country,
  LocaleContextType,
  LocaleProviderProps,
  LocaleSuggestion,
  UserLocalePreferences,
} from '../types/locale';
import { useAuth } from './AuthContext';
import { useConsent } from './ConsentContext';

// Re-export for backward compatibility
export type {
  Country,
  LocaleContextType,
  LocaleSuggestion,
  UserLocalePreferences,
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Default locale preferences (UK-focused as requested)
const DEFAULT_LOCALE: UserLocalePreferences = {
  preferredCountryCode: 'GB',
  preferredCurrency: 'GBP',
  preferredLanguage: 'en-GB',
  preferredTimezone: 'Europe/London',
};

// Basic exchange rates - in production these would come from Kontent CMS
const EXCHANGE_RATES: Record<string, number> = {
  GBP: 1.0, // Base currency
  USD: 1.27,
  EUR: 1.17,
  CAD: 1.71,
  AUD: 1.94,
  SEK: 13.85,
  JPY: 189.5,
  NZD: 2.08,
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: 'C$',
  AUD: 'A$',
  SEK: 'kr',
  JPY: '¥',
  NZD: 'NZ$',
};

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const [currentLocale, setCurrentLocale] =
    useState<UserLocalePreferences>(DEFAULT_LOCALE);
  const [supportedCountries, setSupportedCountries] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();
  const { isEnabled: isConsentEnabled } = useConsent();

  const loadGuestLocalePreferences = useCallback(async () => {
    // UK-only for MVP: always use default UK locale, ignore location suggestion
    if (isConsentEnabled('functional')) {
      const storedPreferences = localStorage.getItem('guestLocalePreferences');
      if (storedPreferences) {
        try {
          const parsed = JSON.parse(storedPreferences);
          // Override to UK regardless of stored preference (MVP: no international)
          setCurrentLocale({
            ...DEFAULT_LOCALE,
            ...parsed,
            preferredCountryCode: 'GB',
            preferredCurrency: 'GBP',
          });
          return;
        } catch (error) {}
      }
    }

    // UK-only: always use UK locale, no geolocation-based suggestion
    setCurrentLocale(DEFAULT_LOCALE);
    if (isConsentEnabled('functional')) {
      localStorage.setItem(
        'guestLocalePreferences',
        JSON.stringify(DEFAULT_LOCALE)
      );
    }
  }, [isConsentEnabled]);

  // Load initial locale preferences and countries
  useEffect(() => {
    const initializeLocale = async () => {
      setIsLoading(true);
      try {
        // Load supported countries
        const countries = await addressService.getCountries();
        setSupportedCountries(countries);

        // Load user preferences if authenticated
        if (isAuthenticated && user) {
          try {
            const userPreferences =
              await authService.getUserLocalePreferences();
            // UK-only for MVP: override to GB/GBP regardless of stored preference
            setCurrentLocale({
              ...userPreferences,
              preferredCountryCode: 'GB',
              preferredCurrency: 'GBP',
            });
          } catch (error) {
            // Fall back to stored preferences or UK default
            await loadGuestLocalePreferences();
          }
        } else {
          // Load guest preferences or suggest based on location
          await loadGuestLocalePreferences();
        }
      } catch (error) {
        // Use defaults on error
        setCurrentLocale(DEFAULT_LOCALE);
      } finally {
        setIsLoading(false);
      }
    };

    initializeLocale();
  }, [isAuthenticated, loadGuestLocalePreferences, user]);

  const updateLocale = async (preferences: Partial<UserLocalePreferences>) => {
    // UK-only for MVP: ignore country/currency changes, keep GB/GBP
    const sanitized = { ...preferences };
    delete (sanitized as Partial<UserLocalePreferences>).preferredCountryCode;
    delete (sanitized as Partial<UserLocalePreferences>).preferredCurrency;
    const updatedLocale = {
      ...currentLocale,
      ...sanitized,
      preferredCountryCode: 'GB',
      preferredCurrency: 'GBP',
    };
    setCurrentLocale(updatedLocale);

    try {
      if (isAuthenticated && user) {
        // Save to user account
        await authService.updateUserLocalePreferences(preferences);
      } else {
        // Save to localStorage for guests only if functional consent enabled
        if (isConsentEnabled('functional')) {
          localStorage.setItem(
            'guestLocalePreferences',
            JSON.stringify(updatedLocale)
          );
        }
      }
    } catch (error) {
      // Revert on failure
      setCurrentLocale(currentLocale);
      throw error;
    }
  };

  const suggestLocaleFromLocation =
    async (): Promise<LocaleSuggestion | null> => {
      try {
        return await localeService.suggestLocaleFromLocation();
      } catch (error) {
        return null;
      }
    };

  const formatCurrency = (amount: number, currency?: string): string => {
    const currencyCode = currency || currentLocale.preferredCurrency;
    const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;

    return `${symbol}${amount.toFixed(2)}`;
  };

  const convertPrice = (
    basePrice: number,
    fromCurrency: string = 'GBP',
    toCurrency?: string
  ): number => {
    const targetCurrency = toCurrency || currentLocale.preferredCurrency;

    if (fromCurrency === targetCurrency) {
      return basePrice;
    }

    // Convert from source currency to GBP (base), then to target currency
    const gbpRate = EXCHANGE_RATES[fromCurrency] || 1;
    const targetRate = EXCHANGE_RATES[targetCurrency] || 1;

    const gbpAmount = basePrice / gbpRate;
    const convertedAmount = gbpAmount * targetRate;

    return Math.round(convertedAmount * 100) / 100; // Round to 2 decimal places
  };

  const contextValue: LocaleContextType = {
    currentLocale,
    supportedCountries,
    isLoading,
    updateLocale,
    suggestLocaleFromLocation,
    formatCurrency,
    convertPrice,
  };

  return (
    <LocaleContext.Provider value={contextValue}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
